// Lowercase throughout. Spec-sheet voice. Kana labels for sections.
// sōtto: lowercase, macron ō, always.

export const translations = {
  en: {
    locale: "en",
    brand: "auditory longevity",
    status: {
      mark: "auditory longevity",
      edition: "edition 01  ·  winter 2025",
      origin: "edition 01  /  a living document",
    },
    sections: {
      hero: { kana: "", roman: "00 / preface" },
      definition: { kana: "", roman: "01 / definition" },
      whynow: { kana: "", roman: "02 / why now" },
      longview: { kana: "", roman: "03 / the long view" },
      principles: { kana: "", roman: "04 / principles" },
      practice: { kana: "", roman: "05 / the practice" },
      literature: { kana: "", roman: "06 / literature" },
      reminder: { kana: "", roman: "07 / a reminder" },
      transition: { kana: "", roman: "08 / transition" },
      join: { kana: "", roman: "09 / join the movement" },
      colophon: { kana: "", roman: "the fine print" },
    },
    hero: {
      meta: "ed. 01  /  ongoing  /  the long view",
      lineA: "the way",
      lineB: "we listen,",
      lineC: "cared for.",
      sub: "",
      hanko: "実 践",
      cta: "begin",
      spec: [
        { k: "size", v: "full life" },
        { k: "color", v: "ink on cream" },
        { k: "edition", v: "01 / 2025" },
        { k: "lang", v: "en · es · ja" },
        { k: "item", v: "al — 001" },
      ],
    },
    definition: {
      heading: "auditory longevity.",
      kicker: "the practice of protecting, recovering, and refining the way we listen and care for hearing and sound exposure as part of long-term health.",
      body:
        "caring for hearing and sound exposure as part of long-term health. a daily-use lifestyle frame, in the same register as sleep, movement, nutrition, or skin.",
      paragraphs: [
        "it reframes hearing from something to be corrected once it fails into something to be maintained across a lifetime, placing it alongside sleep, movement, nutrition, and skin care as a pillar of everyday self-care rather than a purely medical concern.",
        "the concept rests on an established premise: hearing is largely neurological, not merely mechanical. the brain, not the ear alone, does the work of listening, and that work shapes cognition over time. hearing loss has been identified as the single largest modifiable risk factor for dementia (lancet commission, 2020 / 2024); much of it begins early and silently. up to 1.35 billion young people are estimated to be at risk from unsafe listening habits (dillard et al., 2022), and damage often accumulates undetected. a person can pass a standard hearing test while still losing the neural connections that separate speech from noise (kujawa & liberman, 2009).",
        "in practice, auditory longevity is behavioral rather than clinical. it centers on a small set of lifelong habits: protecting hearing in loud environments, allowing recovery afterward, keeping listening volumes moderate, making room for deliberate silence, and engaging with rich and varied sound to keep the listening system active. it is preventive and aspirational in spirit. a discipline practiced before harm is felt, not a treatment applied after.",
        "auditory longevity is distinct from clinical audiology and hearing-loss treatment. it does not diagnose, treat, or cure conditions, and it is not a medical device or medical therapy. it is a framework for proactive, everyday care.",
      ],
      spec: [
        { k: "term", v: "auditory longevity" },
        { k: "use", v: "daily, at any age" },
        { k: "tools", v: "earplugs, monitors, silence" },
        { k: "not", v: "a service, a device, a cure" },
      ],
    },
    whynow: {
      threat: "the danger is that it's invisible until it isn't.",
      hope: "start to take care early. act before you feel it.",
      fadeTitle: "the slow fade.",
      illustrative: "— illustrative",
      fadeYour: "— your hearing",
      fadeNow: "now",
      fadeAxis: "years",
      insight:
        "you don't notice the line falling, because your brain quietly adjusts the floor to match.",
    },
    principles: {
      heading: "three concepts,\nthe whole practice.",
      items: [
        { n: "i.", name: "protect.", body: "protect your hearing in noisy places. avoid long-term exposure to loud sound." },
        { n: "ii.", name: "recover.", body: "after loud, true quiet. minutes, not hours. recovery is where the care happens." },
        { n: "iii.", name: "refine.", body: "listen richly, not loudly. music, nature, conversation, range. keep the listening brain awake." },
      ],
    },
    longview: {
      heading: "longevity.",
      sub: "not louder. longer. the discipline of caring now for what is hardest to notice.",
      stats: [
        { k: "modifiable risk", v: "the largest", unit: "factor for dementia, today.", cite: "lancet commission, 2020 / 2024" },
        { k: "young people at risk", v: "1.35", unit: "billion. mostly from listening habits.", cite: "dillard et al., 2022" },
        { k: "hidden loss", v: "yes", unit: "you can pass a hearing test and still be losing it.", cite: "kujawa & liberman, 2009" },
      ],
      timelineLabel: "an audible lifespan",
      timelineNote: "hearing does not break in a day. it fades so slowly you adapt to each loss. the discipline is caring early.",
      decades: ["20", "30", "40", "50", "60", "70", "80", "90"],
    },
    practice: {
      heading: "ten habits.\nquiet. early. for life.",
      sub:
        "ten things people who plan to keep listening already do. none of them are hard. all of them are early.",
      items: [
        {
          n: "01",
          title: "protect the loud moments.",
          body:
            "concerts, the gym, the subway, a roaring bar. high-fidelity earplugs turn the volume down without killing the night.",
          spec: ["method · physical", "use · on the way out"],
        },
        {
          n: "02",
          title: "honor the after.",
          body:
            "after something loud, give your ears real quiet. not silence then earbuds. recovery is care.",
          spec: ["method · time", "duration · brief"],
        },
        {
          n: "03",
          title: "keep the volume honest.",
          body:
            "around 60% volume, around 60 minutes, then a break. small mercies become a long-life hearing.",
          spec: ["method · attention", "frequency · daily"],
        },
        {
          n: "04",
          title: "collect silence.",
          body:
            "deliberate quiet, minutes not hours, is maintenance, not indulgence. in a world this loud, silence is a luxury you give yourself.",
          spec: ["method · ritual", "frequency · daily"],
        },
        {
          n: "05",
          title: "listen richly, not loudly.",
          body:
            "music, nature, conversation, range. variety keeps the listening brain sharp the way movement keeps a body young.",
          spec: ["method · curation", "scope · ongoing"],
        },
        {
          n: "06",
          title: "audit your tools.",
          body:
            "open-back at home. fitted in-ears outside. monitors at the desk. cheap drivers force you to listen louder to feel anything. better tools, lower volumes.",
          spec: ["method · object", "frequency · once a year"],
        },
        {
          n: "07",
          title: "a baseline check, once.",
          body:
            "a baseline check, the way you do for your eyes and teeth. then on a quiet schedule. a habit, not a diagnosis.",
          spec: ["method · check", "frequency · every few years"],
        },
        {
          n: "08",
          title: "give your ears off-hours.",
          body:
            "headphones are not the default. ears need silence the way muscles need rest. some hours every day, with nothing in them.",
          spec: ["method · rest", "frequency · daily"],
        },
        {
          n: "09",
          title: "train the ear.",
          body:
            "music, language, birdsong. richer input keeps the brain awake to listen. practice the way you practice anything you care about.",
          spec: ["method · attention", "frequency · weekly"],
        },
        {
          n: "10",
          title: "play the long game.",
          body:
            "hearing does not break in a day. it fades so slowly you adapt to each loss. the discipline is caring now.",
          spec: ["method · attitude", "scope · for life"],
        },
      ],
    },
    literature: {
      heading: "on the record.",
      sub: "primary sources only. listed for the careful.",
      items: [
        {
          n: "f.01",
          text:
            "protecting hearing is now considered the single largest modifiable way to lower dementia risk.",
          cite: "lancet commission, 2020 / 2024",
        },
        {
          n: "f.02",
          text:
            "you can pass a standard hearing test and still be losing the wiring that pulls speech out of noise.",
          cite: "kujawa & liberman, 2009",
        },
        {
          n: "f.03",
          text:
            "up to 1.35 billion young people are damaging their hearing right now, mostly from how they listen.",
          cite: "dillard et al., 2022",
        },
        {
          n: "f.04",
          text:
            "the brain has its own repair system for noise. it needs you to give it the conditions.",
          cite: "sitko / goodrich, 2025  ·  shown in animal models",
        },
      ],
      seeAll: "see all sources",
    },
    reminder: {
      heading: "the frequencies fade first.",
      sub: "high frequencies are the first to go. you stop hearing them long before you notice. play the tone. if it sounds faint, that is normal. if it sounds loud, take it as a gift. it is a reminder, not a measurement.",
      play: "[ play 4 khz ]",
      stop: "[ stop ]",
      note:
        "if you do not hear anything, raise the volume slowly. your device might be muted. this is a reminder, not a measurement.",
    },
    transition: {
      heading: "sōtto.",
      copy:
        "a daily listening practice built on these ideas. one way to begin.",
      micro: "a composed auditory practice. not a medical device.",
      cta: "open sōtto",
    },
    plates: {
      light: { tag: "— plate 01", caption: "listen, longer." },
      ripple: { tag: "— plate 02", caption: "the slow fade." },
      horizon: { tag: "— plate 03", caption: "live sharper." },
    },
    join: {
      heading: "join the movement.",
      sub: "",
      placeholder: "your@email",
      submit: "→",
      successNew: "welcome to the practice.",
      successExisting: "you are already in.",
      error: "please enter a valid email.",
    },
    sources: {
      title: "sources",
      subtitle:
        "every claim on this page is backed by primary literature. listed here for the curious and the careful.",
      list: [
        "livingston g., et al. (2020). dementia prevention, intervention, and care: 2020 report of the lancet commission. the lancet, 396(10248), 413–446. updated 2024, 404(10452), 572–628.",
        "dillard l. k., et al. (2022). prevalence and global estimates of unsafe listening practices in adolescents and young adults. bmj global health, 7(11), e010501.",
        "lin f. r., et al. (2014). association of hearing impairment with brain volume changes in older adults. neuroimage, 90, 84–92.",
        "kujawa s. g., & liberman m. c. (2009). adding insult to injury: cochlear nerve degeneration after \"temporary\" noise-induced hearing loss. the journal of neuroscience, 29(45), 14077–14085.",
        "60/60 rule. endorsed as guidance by the hearing health foundation and asha. who uses an absolute dose instead (≈80 db / 40 hrs per week, make listening safe).",
        "u.s. epa (1974), levels document; adopted in who (1999), guidelines for community noise; echoed by cdc.",
        "sitko a. s., frank m. m., romero g. e., hunt m., & goodrich l. v. (2025). lateral olivocochlear neurons modulate cochlear responses to noise exposure. pnas, 122(4), e2404558122. animal model.",
      ],
      close: "close",
    },
    colophon: {
      definition:
        "auditory longevity is the practice of protecting, recovering, and refining the way we listen and care for hearing and sound exposure as part of long-term health.",
      attribution:
        "an initiative by the team behind sōtto, to build consciousness around how we listen.",
      disclaimer:
        "this site is educational and is not medical advice, diagnosis, or treatment. it does not replace assessment by a qualified audiologist or physician. if you have sudden changes in hearing, pain, or distressing symptoms, see a professional. nothing here is a medical device or a cure.",
      copy: "© auditory longevity",
      printedIn: "a daily practice  ·  live sharper",
    },
  },

  es: {
    locale: "es",
    brand: "longevidad auditiva",
    status: {
      mark: "longevidad auditiva",
      edition: "edición 01  ·  invierno 2025",
      origin: "edición 01  /  un documento vivo",
    },
    sections: {
      hero: { kana: "", roman: "00 / prólogo" },
      definition: { kana: "", roman: "01 / definición" },
      whynow: { kana: "", roman: "02 / por qué ahora" },
      longview: { kana: "", roman: "03 / la mirada larga" },
      principles: { kana: "", roman: "04 / principios" },
      practice: { kana: "", roman: "05 / la práctica" },
      literature: { kana: "", roman: "06 / literatura" },
      reminder: { kana: "", roman: "07 / recordatorio" },
      transition: { kana: "", roman: "08 / transición" },
      join: { kana: "", roman: "09 / únete al movimiento" },
      colophon: { kana: "", roman: "letra pequeña" },
    },
    hero: {
      meta: "ed. 01  /  en curso  /  la mirada larga",
      lineA: "la forma",
      lineB: "en que escuchamos,",
      lineC: "cuidada.",
      sub: "",
      hanko: "実 践",
      cta: "empezar",
      spec: [
        { k: "talla", v: "toda la vida" },
        { k: "color", v: "tinta sobre crema" },
        { k: "edición", v: "01 / 2025" },
        { k: "idioma", v: "en · es · ja" },
        { k: "ítem", v: "al — 001" },
      ],
    },
    definition: {
      heading: "longevidad auditiva.",
      kicker:
        "la práctica de proteger, recuperar y refinar la forma en que escuchamos y cuidar la audición y la exposición al sonido como parte de la salud a largo plazo.",
      body:
        "cuidar la audición y la exposición al sonido como parte de la salud a largo plazo. un marco de uso diario, en el mismo registro que el sueño, el movimiento, la nutrición o la piel.",
      paragraphs: [
        "replantea la audición: deja de ser algo que se corrige cuando falla y pasa a ser algo que se mantiene a lo largo de la vida, junto al sueño, el movimiento, la nutrición y el cuidado de la piel, como un pilar del autocuidado cotidiano más que una preocupación puramente médica.",
        "la idea descansa sobre una premisa establecida: la audición es en gran medida neurológica, no solo mecánica. el cerebro, no solo el oído, hace el trabajo de escuchar, y ese trabajo da forma a la cognición con el tiempo. la pérdida auditiva ha sido identificada como el mayor factor de riesgo modificable conocido para la demencia (lancet commission, 2020 / 2024); gran parte de ella comienza temprano y en silencio. se estima que hasta 1.35 mil millones de jóvenes están en riesgo por hábitos de escucha inseguros (dillard et al., 2022), y el daño suele acumularse sin detectarse, ya que una persona puede pasar una prueba auditiva estándar y aún así estar perdiendo las conexiones neuronales que separan el habla del ruido (kujawa & liberman, 2009).",
        "en la práctica, la longevidad auditiva es conductual, no clínica. se centra en un pequeño conjunto de hábitos para toda la vida: proteger la audición en entornos ruidosos, permitir la recuperación después, mantener volúmenes de escucha moderados, hacer espacio para silencio deliberado y relacionarse con sonido rico y variado para mantener el sistema auditivo activo. es preventiva y aspiracional en espíritu. una disciplina practicada antes de sentir el daño, no un tratamiento aplicado después.",
        "la longevidad auditiva es distinta de la audiología clínica y del tratamiento de la pérdida auditiva. no diagnostica, trata ni cura afecciones, y no es un dispositivo ni una terapia médica. es un marco para el cuidado proactivo y cotidiano.",
      ],
      spec: [
        { k: "término", v: "longevidad auditiva" },
        { k: "uso", v: "diario, a cualquier edad" },
        { k: "herramientas", v: "tapones, monitores, silencio" },
        { k: "no es", v: "servicio, dispositivo, cura" },
      ],
    },
    whynow: {
      threat: "el peligro es que es invisible, hasta que deja de serlo.",
      hope: "empieza a cuidarte pronto. actúa antes de sentirlo.",
      fadeTitle: "la caída lenta.",
      illustrative: "— ilustrativo",
      fadeYour: "— tu audición",
      fadeNow: "ahora",
      fadeAxis: "años",
      insight:
        "no notas que la línea baja, porque tu cerebro ajusta silenciosamente el suelo para que coincida.",
    },
    principles: {
      heading: "tres conceptos,\ntoda la práctica.",
      items: [
        { n: "i.", name: "proteger.", body: "protege tu audición en lugares ruidosos. evita la exposición prolongada al sonido fuerte." },
        { n: "ii.", name: "recuperar.", body: "tras lo ruidoso, silencio verdadero. minutos, no horas. la recuperación es donde ocurre el cuidado." },
        { n: "iii.", name: "refinar.", body: "escucha rico, no fuerte. música, naturaleza, conversación, rango. mantén despierto el cerebro auditivo." },
      ],
    },
    longview: {
      heading: "longevidad.",
      sub: "no más fuerte. más tiempo. la disciplina de cuidar ahora lo que cuesta más notar.",
      stats: [
        { k: "factor modificable", v: "el mayor", unit: "conocido para reducir el riesgo de demencia.", cite: "lancet commission, 2020 / 2024" },
        { k: "jóvenes en riesgo", v: "1.35", unit: "mil millones. sobre todo por cómo escuchan.", cite: "dillard et al., 2022" },
        { k: "pérdida oculta", v: "sí", unit: "puedes pasar una prueba auditiva y aún así estar perdiéndolo.", cite: "kujawa & liberman, 2009" },
      ],
      timelineLabel: "una vida audible",
      timelineNote: "la audición no se rompe en un día. se desvanece tan lentamente que te adaptas a cada pérdida. la disciplina es cuidar pronto.",
      decades: ["20", "30", "40", "50", "60", "70", "80", "90"],
    },
    practice: {
      heading: "diez hábitos.\nsilenciosos. a tiempo. de por vida.",
      sub:
        "diez cosas que ya hacen quienes piensan seguir escuchando. ninguna es difícil. todas son a tiempo.",
      items: [
        {
          n: "01",
          title: "protege los momentos ruidosos.",
          body:
            "conciertos, gimnasio, metro, un bar atronador. los tapones de alta fidelidad bajan el volumen sin matar la noche.",
          spec: ["método · físico", "uso · al salir"],
        },
        {
          n: "02",
          title: "honra el después.",
          body:
            "tras algo ruidoso, dale a tus oídos un silencio real. no silencio y enseguida auriculares. la recuperación es cuidado.",
          spec: ["método · tiempo", "duración · breve"],
        },
        {
          n: "03",
          title: "mantén el volumen honesto.",
          body:
            "en torno al 60% de volumen, unos 60 minutos, y descanso. pequeñas mercedes que se convierten en una audición larga.",
          spec: ["método · atención", "frecuencia · diaria"],
        },
        {
          n: "04",
          title: "colecciona silencio.",
          body:
            "silencio deliberado, minutos no horas, es mantenimiento, no lujo. en un mundo así de ruidoso, el silencio es un lujo que te regalas.",
          spec: ["método · ritual", "frecuencia · diaria"],
        },
        {
          n: "05",
          title: "escucha rico, no fuerte.",
          body:
            "música, naturaleza, conversación, rango. la variedad mantiene el cerebro auditivo afilado como el movimiento mantiene joven al cuerpo.",
          spec: ["método · curaduría", "alcance · continuo"],
        },
        {
          n: "06",
          title: "audita tus herramientas.",
          body:
            "abiertos en casa. in-ears con buen sellado al salir. monitores en el escritorio. los drivers baratos te obligan a subir el volumen. mejor equipo, menos volumen.",
          spec: ["método · objeto", "frecuencia · una vez al año"],
        },
        {
          n: "07",
          title: "una revisión de referencia, una vez.",
          body:
            "una revisión de referencia, como haces con los ojos y los dientes. y luego con un calendario tranquilo. un hábito, no un diagnóstico.",
          spec: ["método · revisión", "frecuencia · cada pocos años"],
        },
        {
          n: "08",
          title: "dale horas libres a tus oídos.",
          body:
            "los auriculares no son el estado por defecto. los oídos necesitan silencio como los músculos necesitan descanso. unas horas cada día, sin nada dentro.",
          spec: ["método · descanso", "frecuencia · diaria"],
        },
        {
          n: "09",
          title: "entrena el oído.",
          body:
            "música, idiomas, pájaros. un estímulo más rico mantiene el cerebro despierto para escuchar. practica como practicas lo que te importa.",
          spec: ["método · atención", "frecuencia · semanal"],
        },
        {
          n: "10",
          title: "juega el partido largo.",
          body:
            "la audición no se rompe en un día. se desvanece tan lentamente que te adaptas a cada pérdida. la disciplina es cuidar ahora.",
          spec: ["método · actitud", "alcance · de por vida"],
        },
      ],
    },
    literature: {
      heading: "en acta.",
      sub: "solo fuentes primarias. listadas para los cuidadosos.",
      items: [
        {
          n: "f.01",
          text:
            "proteger la audición se considera hoy la mayor forma modificable conocida de reducir el riesgo de demencia.",
          cite: "lancet commission, 2020 / 2024",
        },
        {
          n: "f.02",
          text:
            "puedes pasar una prueba auditiva estándar y aún así estar perdiendo el cableado que separa el habla del ruido.",
          cite: "kujawa & liberman, 2009",
        },
        {
          n: "f.03",
          text:
            "hasta 1.35 mil millones de jóvenes están dañando su audición ahora mismo, sobre todo por cómo escuchan.",
          cite: "dillard et al., 2022",
        },
        {
          n: "f.04",
          text:
            "el cerebro tiene su propio equipo de reparación para el ruido. solo necesita que le des las condiciones.",
          cite: "sitko / goodrich, 2025  ·  modelos animales",
        },
      ],
      seeAll: "ver todas las fuentes",
    },
    reminder: {
      heading: "las frecuencias se van primero.",
      sub: "las frecuencias altas son las primeras en irse. dejas de oírlas mucho antes de notarlo. toca el tono. si lo oyes flojo, es normal. si lo oyes fuerte, tómalo como un regalo. esto es un recordatorio, no una medición.",
      play: "[ tocar 4 khz ]",
      stop: "[ detener ]",
      note:
        "si no oyes nada, sube el volumen poco a poco. puede que tu dispositivo esté en silencio. esto es un recordatorio, no una medición.",
    },
    transition: {
      heading: "sōtto.",
      copy:
        "una práctica diaria de escucha construida sobre estas ideas. una forma de empezar.",
      micro: "una práctica auditiva compuesta. no es un dispositivo médico.",
      cta: "entrar a sōtto",
    },
    plates: {
      light: { tag: "— plate 01", caption: "escucha, más." },
      ripple: { tag: "— plate 02", caption: "la caída lenta." },
      horizon: { tag: "— plate 03", caption: "vive con más nitidez." },
    },
    join: {
      heading: "únete al movimiento.",
      sub: "",
      placeholder: "tu@email",
      submit: "→",
      successNew: "bienvenida a la práctica.",
      successExisting: "ya estás dentro.",
      error: "introduce un email válido.",
    },
    sources: {
      title: "fuentes",
      subtitle:
        "cada afirmación de esta página está respaldada por literatura primaria. listada aquí para los curiosos y los cuidadosos.",
      list: [
        "livingston g., et al. (2020). dementia prevention, intervention, and care: 2020 report of the lancet commission. the lancet, 396(10248), 413–446. actualizado 2024, 404(10452), 572–628.",
        "dillard l. k., et al. (2022). prevalence and global estimates of unsafe listening practices in adolescents and young adults. bmj global health, 7(11), e010501.",
        "lin f. r., et al. (2014). association of hearing impairment with brain volume changes in older adults. neuroimage, 90, 84–92.",
        "kujawa s. g., & liberman m. c. (2009). adding insult to injury: cochlear nerve degeneration after \"temporary\" noise-induced hearing loss. the journal of neuroscience, 29(45), 14077–14085.",
        "regla 60/60. respaldada como guía por la hearing health foundation y asha. la oms usa una dosis absoluta (≈80 db / 40 h por semana, make listening safe).",
        "u.s. epa (1974); who (1999); cdc.",
        "sitko a. s., et al. (2025). pnas, 122(4), e2404558122. modelo animal.",
      ],
      close: "cerrar",
    },
    colophon: {
      definition:
        "la longevidad auditiva es la práctica de proteger, recuperar y refinar la forma en que escuchamos y cuidar la audición y la exposición al sonido como parte de la salud a largo plazo.",
      attribution:
        "una iniciativa del equipo detrás de sōtto, para crear conciencia sobre cómo escuchamos.",
      disclaimer:
        "este sitio es educativo y no constituye consejo, diagnóstico o tratamiento médico. no reemplaza la evaluación por un audiólogo o médico cualificado. si tienes cambios repentinos de audición, dolor o síntomas inquietantes, acude a un profesional. nada aquí es un dispositivo médico ni una cura.",
      copy: "© auditory longevity",
      printedIn: "una práctica diaria  ·  vive con más nitidez",
    },
  },

  ja: {
    locale: "ja",
    brand: "聴覚の長寿",
    status: {
      mark: "聴覚の長寿",
      edition: "第 01 号  ·  2025 冬",
      origin: "第 01 号  /  生きた文書",
    },
    sections: {
      hero: { kana: "", roman: "00 / 序" },
      definition: { kana: "", roman: "01 / 定義" },
      whynow: { kana: "", roman: "02 / なぜ今" },
      longview: { kana: "", roman: "03 / 長い視座" },
      principles: { kana: "", roman: "04 / 原則" },
      practice: { kana: "", roman: "05 / 実践" },
      literature: { kana: "", roman: "06 / 文献" },
      reminder: { kana: "", roman: "07 / リマインダー" },
      transition: { kana: "", roman: "08 / 移行" },
      join: { kana: "", roman: "09 / ムーブメントに参加" },
      colophon: { kana: "", roman: "細部" },
    },
    hero: {
      meta: "第 01 号  /  継続中  /  長い視座",
      lineA: "聴き方を、",
      lineB: "大切に。",
      lineC: "これから、一生。",
      sub: "",
      hanko: "実 践",
      cta: "はじめる",
      spec: [
        { k: "size", v: "一生" },
        { k: "color", v: "墨/クリーム" },
        { k: "edition", v: "01 / 2025" },
        { k: "lang", v: "en · es · ja" },
        { k: "item", v: "al — 001" },
      ],
    },
    definition: {
      heading: "聴覚の長寿。",
      kicker:
        "聴き方を守り、回復させ、洗練させる実践であり、聞こえと音への接し方を長期的な健康の一部としてケアすること。",
      body:
        "聞こえと音への接し方を、長期的な健康の一部としてケアすること。睡眠、運動、栄養、肌と同じレジスターで扱う、日々の生活様式の枠組み。",
      paragraphs: [
        "聴覚を、失われてから直すものから、人生を通じて維持するものへと位置づけ直す。睡眠、運動、栄養、スキンケアと並ぶ、日々のセルフケアの柱として、純粋に医療的な関心事ではなく。",
        "この考え方は確立された前提に立つ。聴覚は主として神経学的であり、機械的なものだけではない。聴くという働きは耳だけでなく脳が担い、その働きが時間とともに認知のかたちを変える。聴覚の喪失は、認知症の修正可能な最大の単一リスク要因として特定されている（lancet commission, 2020 / 2024）。そしてその多くは、早期に、静かに始まる。世界では最大13億5,000万人の若者が、安全でない聴き方によってリスクにさらされていると推定されており（dillard et al., 2022）、被害はしばしば気づかれぬまま積み重なる。標準的な聴力検査をパスしても、雑音から音声を聞き分ける神経の結合が失われ続けていることがあるからだ（kujawa & liberman, 2009）。",
        "実践においては、聴覚の長寿は臨床的なものではなく、行動に基づくものだ。生涯にわたる少数の習慣を中心にする。騒がしい環境で聴覚を守る、その後に回復のための時間を許す、聴く音量を適度に保つ、意図された静けさのための余地をつくる、そして聴くシステムを活発に保つために豊かで多様な音と関わる。予防的で、志向的なもの。害を感じる前に実践する規律であり、後から施される治療ではない。",
        "聴覚の長寿は、臨床聴覚学や聴覚損失の治療とは異なる。状態を診断・治療・治癒するものではなく、医療機器でも医療療法でもない。能動的で日常的なケアのための枠組みである。",
      ],
      spec: [
        { k: "term", v: "聴覚の長寿" },
        { k: "use", v: "毎日、何歳でも" },
        { k: "tools", v: "耳栓、モニター、静けさ" },
        { k: "not", v: "サービス、機器、治療ではない" },
      ],
    },
    whynow: {
      threat: "危険なのは、それが見えないということだ。見えるようになるまでは。",
      hope: "早めにケアを始めること。感じる前に動くこと。",
      fadeTitle: "ゆるやかな喪失。",
      illustrative: "— 概念図",
      fadeYour: "— あなたの聴覚",
      fadeNow: "今",
      fadeAxis: "年齢",
      insight:
        "線が下がっていることに気づかないのは、脳が静かに足元の高さを合わせているから。",
    },
    principles: {
      heading: "三つの概念、\n実践のすべて。",
      items: [
        { n: "i.", name: "守る。", body: "騒がしい場所で聴覚を守る。長時間の大音量への露出を避ける。" },
        { n: "ii.", name: "回復させる。", body: "大きな音のあと、本当の静けさを。何時間ではなく数分。回復こそケアの時間。" },
        { n: "iii.", name: "洗練させる。", body: "大きくではなく、豊かに。音楽、自然、会話、レンジ。聴く脳を起こしておく。" },
      ],
    },
    longview: {
      heading: "長寿。",
      sub: "大きくではなく、長く。気づきにくいものを、今から大切にする規律。",
      stats: [
        { k: "修正可能な要因", v: "最大", unit: "認知症リスクを下げる、現在知られる要因のなかで。", cite: "lancet commission, 2020 / 2024" },
        { k: "リスクのある若者", v: "1.35", unit: "億人。多くは聴き方のせい。", cite: "dillard et al., 2022" },
        { k: "隠れた損失", v: "あり", unit: "標準的な聴力検査をパスしても、失いつつあることがある。", cite: "kujawa & liberman, 2009" },
      ],
      timelineLabel: "聴こえる一生",
      timelineNote: "聴覚は一日では壊れない。あまりにゆっくり褪せるから、人はそれに順応してしまう。規律は、早めにケアすること。",
      decades: ["20", "30", "40", "50", "60", "70", "80", "90"],
    },
    practice: {
      heading: "十の習慣。\n静か。早めに。一生。",
      sub:
        "これからも聴き続けるつもりの人たちが、すでにやっていること。どれも難しくない。どれも早めにやれる。",
      items: [
        {
          n: "01",
          title: "うるさい瞬間を守る。",
          body:
            "ライブ、ジム、地下鉄、騒がしいバー。ハイファイの耳栓は、夜を殺さずに音量だけを下げてくれる。",
          spec: ["method · physical", "use · 出かける時"],
        },
        {
          n: "02",
          title: "「あと」を大切に。",
          body:
            "うるさいことのあとには、本当の静けさを耳に。沈黙のすぐあとにイヤホン、ではなく。回復こそケア。",
          spec: ["method · time", "duration · 短く"],
        },
        {
          n: "03",
          title: "音量に誠実であれ。",
          body:
            "およそ60%の音量、およそ60分、そして休憩。小さな思いやりが、長く聴ける耳をつくる。",
          spec: ["method · attention", "frequency · 毎日"],
        },
        {
          n: "04",
          title: "静けさを集める。",
          body:
            "意図された静けさ、何時間ではなく数分、は贅沢ではなくメンテナンス。これほど騒がしい世界で、静けさは自分への贅沢。",
          spec: ["method · ritual", "frequency · 毎日"],
        },
        {
          n: "05",
          title: "大きくではなく、豊かに。",
          body:
            "運動が身体を若く保つように、多様さが聴く脳を鋭く保つ。音量ではなく、幅。音楽、自然、会話、レンジ。",
          spec: ["method · curation", "scope · 継続"],
        },
        {
          n: "06",
          title: "道具を見直す。",
          body:
            "家ではオープンバック。外ではフィットの良いイヤホン。机ではモニター。良い道具で、低い音量で。",
          spec: ["method · object", "frequency · 年に一度"],
        },
        {
          n: "07",
          title: "基準値を、一度。",
          body:
            "基準値を一度とっておく。目や歯と同じこと。その後は静かな間隔で。診断ではなく、習慣として。",
          spec: ["method · check", "frequency · 数年ごと"],
        },
        {
          n: "08",
          title: "耳に休みを。",
          body:
            "ヘッドフォンは既定の状態ではない。筋肉に休息が必要なように、耳には静けさが必要。毎日、何時間かは、耳に何も入れない時間を。",
          spec: ["method · rest", "frequency · 毎日"],
        },
        {
          n: "09",
          title: "耳を鍛える。",
          body:
            "音楽、言語、鳥の声。入力が豊かなほど、脳は長く聴く側のままでいられる。大切にしているもののように、聴き方も練習する。",
          spec: ["method · attention", "frequency · 週ごと"],
        },
        {
          n: "10",
          title: "長い試合を戦え。",
          body:
            "聴覚は一日では壊れない。あまりにゆっくり褪せるから、人はその喪失に順応してしまう。本当の規律は、今ケアすること。",
          spec: ["method · attitude", "scope · 一生"],
        },
      ],
    },
    literature: {
      heading: "記録に残っている。",
      sub: "一次文献のみ。慎重な読者のために。",
      items: [
        {
          n: "f.01",
          text:
            "聴覚を守ることは、認知症リスクを下げるうえで、現在知られる修正可能な要因のなかで最大とされている。",
          cite: "lancet commission, 2020 / 2024",
        },
        {
          n: "f.02",
          text:
            "標準的な聴力検査をパスしても、騒音の中から声を拾い出す配線が失われていることがある。",
          cite: "kujawa & liberman, 2009",
        },
        {
          n: "f.03",
          text:
            "最大13億5,000万人の若者が、いま聴覚を傷つけている。その多くは聴き方のせい。",
          cite: "dillard et al., 2022",
        },
        {
          n: "f.04",
          text:
            "脳には騒音への修復チームさえ備わっている。必要なのは、その条件を与えることだけ。",
          cite: "sitko / goodrich, 2025  ·  動物モデル",
        },
      ],
      seeAll: "すべての出典を見る",
    },
    reminder: {
      heading: "高い音から、先に消える。",
      sub: "高周波は最初に失われる音です。気づくよりずっと前に聴こえなくなる。トーンを再生してみてください。かすかなら、それで普通。よく聴こえたなら、贈り物だと受け取って。これは計測ではなく、リマインダーです。",
      play: "[ 4 khz を再生 ]",
      stop: "[ 停止 ]",
      note:
        "何も聞こえないときは、ゆっくり音量を上げてみてください。端末がミュートかもしれません。これは計測ではなく、リマインダーです。",
    },
    transition: {
      heading: "sōtto。",
      copy:
        "これらの考え方の上に立つ、日々の聴くプラクティス。始め方のひとつ。",
      micro: "整えられた聴覚のプラクティス。医療機器ではありません。",
      cta: "sōtto へ",
    },
    plates: {
      light: { tag: "— plate 01", caption: "より長く聴く。" },
      ripple: { tag: "— plate 02", caption: "ゆるやかな喪失。" },
      horizon: { tag: "— plate 03", caption: "鋭く生きる。" },
    },
    join: {
      heading: "ムーブメントに参加する。",
      sub: "",
      placeholder: "your@email",
      submit: "→",
      successNew: "ようこそ、プラクティスへ。",
      successExisting: "すでに登録されています。",
      error: "有効なメールアドレスを入力してください。",
    },
    sources: {
      title: "出典",
      subtitle: "このページのすべての主張は一次文献に基づきます。慎重な読者のために。",
      list: [
        "livingston g., et al. (2020/2024). lancet commission. the lancet 396(10248), 413–446; 404(10452), 572–628.",
        "dillard l. k., et al. (2022). bmj global health 7(11), e010501.",
        "lin f. r., et al. (2014). neuroimage 90, 84–92.",
        "kujawa s. g., & liberman m. c. (2009). j neurosci 29(45), 14077–14085.",
        "60/60ルール。hearing health foundation、ashaによるガイダンス。whoは絶対線量を採用（≈80 db / 週40時間、make listening safe）。",
        "u.s. epa (1974); who (1999); cdc。",
        "sitko a. s., et al. (2025). pnas 122(4), e2404558122. 動物モデル。",
      ],
      close: "閉じる",
    },
    colophon: {
      definition:
        "聴覚の長寿とは、聴き方を守り、回復させ、洗練させる実践であり、聞こえと音への接し方を、長期的な健康の一部としてケアすること。",
      attribution:
        "sōttoを支えるチームによるイニシアチブ。聴き方への意識を育てるために。",
      disclaimer:
        "本サイトは教育目的であり、医療的なアドバイス、診断、治療ではありません。資格を持つ聴覚専門家・医師の評価に代わるものではありません。突然の聴力の変化、痛み、不快な症状がある場合は専門家へ。本サイトの内容は医療機器でも治療法でもありません。",
      copy: "© auditory longevity",
      printedIn: "日々のプラクティス  ·  鋭く生きる",
    },
  },
};
